import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { useScopedPath, useSlugScope } from "@/lib/scoped";
import { ArrowLeft, Crown, Medal, Trophy } from "lucide-react";
import { toast } from "sonner";

function formatPounds(pence) {
  if (!pence) return "£0.00";
  return `£${(pence / 100).toFixed(2)}`;
}

function shortDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function RankBadge({ rank }) {
  if (rank === 1) {
    return (
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-sm bg-gradient-to-b from-amber-400 to-amber-600 text-white shadow">
        <Crown className="h-4 w-4" />
      </span>
    );
  }
  if (rank === 2) {
    return (
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-sm bg-zinc-200 text-zinc-800 font-heading text-sm font-bold">
        <Medal className="h-4 w-4" />
      </span>
    );
  }
  if (rank === 3) {
    return (
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-sm bg-amber-100 text-amber-800 font-heading text-sm font-bold">
        <Medal className="h-4 w-4" />
      </span>
    );
  }
  return (
    <span className="inline-flex h-8 w-8 items-center justify-center rounded-sm border border-zinc-300 text-zinc-700 font-heading text-sm font-bold tabular-nums">
      {rank}
    </span>
  );
}

export default function SeasonArchiveDetailPage() {
  const scope = useScopedPath();
  const slug = useSlugScope();
  const { seasonId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [closure, setClosure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // iter96 — thread the URL slug through both calls so the archive
        // detail (and the sibling closure lookup) stay strictly filtered
        // to the current tenant.
        const qp = slug ? `?scope_slug=${encodeURIComponent(slug)}` : "";
        const [lb, all] = await Promise.all([
          api.get(`/seasons/${seasonId}/leaderboard${qp}`),
          api.get(`/champions${qp}`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setData(lb.data);
        const match = (all.data || []).find(
          (c) => (c.season_id || c.id) === seasonId
        );
        setClosure(match || null);
        if (!lb.data || (lb.data.standings || []).length === 0) {
          setNotFound(!match && (!(lb.data?.standings) || lb.data.standings.length === 0));
        }
      } catch (e) {
        if (!cancelled) {
          toast.error(formatApiError(e));
          setNotFound(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [seasonId, slug]);

  const standings = data?.standings || [];
  const champion = closure?.champion || (standings[0] || null);
  const seasonName = closure?.season_name || (data?.season_name) || "Season";
  const closedAt = closure?.closed_at || data?.closed_at;
  const isSnapshot = data?.is_snapshot;

  const totals = useMemo(() => {
    const entrants = standings.length;
    const topScore = standings[0]?.total_points || 0;
    const matchdays = Math.max(0, ...standings.map((s) => s.matches_played || 0));
    return { entrants, topScore, matchdays };
  }, [standings]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="season-archive-detail-page">
        <Link to={scope("/seasons-archive")} className="text-xs font-semibold text-[#0038FF] hover:underline inline-flex items-center gap-1.5" data-testid="archive-detail-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to past seasons
        </Link>

        {loading ? (
          <p className="mt-10 text-sm uppercase tracking-[0.18em] text-zinc-500">Loading…</p>
        ) : notFound || !data ? (
          <div className="mt-10 border border-dashed border-zinc-300 rounded-sm p-16 text-center" data-testid="archive-detail-not-found">
            <p className="font-heading text-2xl font-semibold text-zinc-950">Season not found.</p>
            <p className="mt-2 text-sm text-zinc-500">
              We couldn&apos;t find a closed season with that ID.
            </p>
          </div>
        ) : (
          <>
            {/* Champion banner */}
            <header className="mt-6 mb-8 relative overflow-hidden border border-amber-300 rounded-sm bg-gradient-to-br from-amber-50 via-white to-amber-50 p-6 sm:p-8" data-testid="archive-detail-champion-banner">
              <span className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-amber-300/30 blur-3xl" aria-hidden="true" />
              <div className="flex items-start gap-4 sm:gap-6 flex-wrap">
                <span className="inline-flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-sm bg-gradient-to-b from-amber-400 to-amber-600 text-white shadow shrink-0">
                  <Trophy className="h-7 w-7 sm:h-8 sm:w-8" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-amber-800">Season Champion</p>
                  <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-950 mt-1 truncate" data-testid="archive-detail-season-name">
                    {seasonName}
                  </h1>
                  <p className="text-sm text-zinc-600 mt-1">
                    Closed <strong>{shortDate(closedAt) || "—"}</strong>
                  </p>
                  <div className="mt-5">
                    <p className="text-xs text-zinc-500">Champion</p>
                    <p className="font-heading text-2xl sm:text-3xl font-bold text-zinc-950 mt-0.5 truncate" data-testid="archive-detail-champion-name">
                      {champion?.name || champion?.user_name || "—"}
                    </p>
                    {(champion?.total_points != null) && (
                      <p className="text-xs text-zinc-500 mt-1 tabular-nums">
                        {champion.total_points} pts · {champion.matches_played || 0} match-days
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Snapshot quick stats */}
              <div className="mt-6 grid grid-cols-3 gap-3 sm:gap-4" data-testid="archive-detail-stats">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Entrants</p>
                  <p className="font-heading text-xl sm:text-2xl font-bold text-zinc-950 mt-0.5 tabular-nums">{totals.entrants}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Top score</p>
                  <p className="font-heading text-xl sm:text-2xl font-bold text-zinc-950 mt-0.5 tabular-nums">{totals.topScore}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Match-days</p>
                  <p className="font-heading text-xl sm:text-2xl font-bold text-zinc-950 mt-0.5 tabular-nums">{totals.matchdays}</p>
                </div>
              </div>
            </header>

            {/* Final standings table */}
            <section data-testid="archive-detail-standings">
              <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">Final standings</h2>
                {!isSnapshot && (
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500" data-testid="archive-detail-live-note">
                    Live computation · season still active
                  </p>
                )}
              </div>

              {standings.length === 0 ? (
                <p className="text-sm text-zinc-500" data-testid="archive-detail-empty">
                  No standings were recorded for this season.
                </p>
              ) : (
                <div className="border border-zinc-200 rounded-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                      <tr>
                        <th className="text-left px-3 py-3 w-16">Rank</th>
                        <th className="text-left px-3 py-3">Player</th>
                        <th className="text-right px-3 py-3 w-24">Played</th>
                        <th
                          className="text-right px-3 py-3 w-24"
                          title="Number of individual stat predictions hit at the max (3 pts on goals/shots/corners/offsides/fouls, 2 pts on throw-ins). Used as the 3rd tie-break."
                        >
                          Correct
                        </th>
                        <th className="text-right px-3 py-3 w-24">Points</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {standings.map((s, idx) => {
                        const rank = idx + 1;
                        const isMe = user && (user.id === s.user_id);
                        return (
                          <tr
                            key={s.user_id || `${s.user_name}-${idx}`}
                            data-testid={`archive-detail-row-${rank}`}
                            className={isMe ? "bg-blue-50" : ""}
                          >
                            <td className="px-3 py-2">
                              <RankBadge rank={rank} />
                            </td>
                            <td className="px-3 py-2 font-medium text-zinc-950">
                              <span className="truncate inline-block max-w-[260px] align-middle" data-testid="archive-detail-row-name">
                                {s.user_name || "—"}
                              </span>
                              {isMe && (
                                <span className="ml-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#0038FF]">You</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-zinc-700">{s.matches_played || 0}</td>
                            <td
                              className="px-3 py-2 text-right tabular-nums text-zinc-700"
                              data-testid={`archive-detail-row-correct-${rank}`}
                            >
                              {s.correct_count ?? 0}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums font-bold text-zinc-950">{s.total_points || 0}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <div className="mt-10 flex items-center gap-4 flex-wrap">
              <Link to={scope("/champions")} className="text-sm font-semibold text-[#0038FF] underline inline-flex items-center gap-1.5" data-testid="archive-detail-hof-link">
                <Trophy className="h-4 w-4" /> View the Hall of Fame
              </Link>
              <span className="text-zinc-300">·</span>
              <Link to={scope("/leaderboard")} className="text-sm font-semibold text-[#0038FF] underline" data-testid="archive-detail-current-link">
                Current Overall League →
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
